import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, type ExtensionToken } from "../lib/api";
import { whenLast } from "../lib/dates";

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Install and connect the KuduRMS browser extension: a side panel that adds the person on a
 * LinkedIn, GitHub or other profile page, spots people already here, and shortlists them.
 */
export function BrowserExtension() {
  const [tokens, setTokens] = useState<ExtensionToken[] | null>(null);
  const [label, setLabel] = useState("Chrome");
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const load = useAction();
  const act = useAction();

  const reload = useCallback(() => load.run(async () => setTokens((await api.extensionTokens()).tokens)), [load]);
  useEffect(() => void reload(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = (e: FormEvent) => {
    e.preventDefault();
    void act.run(async () => {
      const { token } = await api.createExtensionToken(label.trim() || "Browser extension");
      setCreated(token.token);
      setCopied(false);
      await reload();
    });
  };

  const revoke = (t: ExtensionToken) =>
    void act.run(async () => {
      await api.revokeExtensionToken(t.id);
      await reload();
    });

  return (
    <>
      <div className="page-head">
        <p className="label">Browser extension</p>
        <h1 className="display">
          Add people from <em>their profile</em>.
        </h1>
        <p className="lede">
          A side panel for Chrome, Edge and Brave. Open someone's LinkedIn or GitHub profile and it shows whether they're already in
          KuduRMS. If they are, you can shortlist them, update their details or log a note. If not, you can add them with one click.
        </p>
      </div>

      <section className="section" aria-labelledby="install-h">
        <div className="section-head">
          <h2 id="install-h" className="label">
            Install
          </h2>
        </div>
        <ol className="index steps">
          <li>
            <span className="num" aria-hidden>
              01
            </span>
            <span className="main">
              <span className="primary">
                <a href="/kudu-extension.zip" download>
                  Download the extension
                </a>{" "}
                and unzip it somewhere it can stay, such as your Documents folder.
              </span>
            </span>
          </li>
          <li>
            <span className="num" aria-hidden>
              02
            </span>
            <span className="main">
              <span className="primary">
                Open <code>chrome://extensions</code> (in Edge, <code>edge://extensions</code>) and switch on <strong>Developer mode</strong>.
              </span>
            </span>
          </li>
          <li>
            <span className="num" aria-hidden>
              03
            </span>
            <span className="main">
              <span className="primary">
                Choose <strong>Load unpacked</strong> and pick the <code>kudu-extension</code> folder.
              </span>
            </span>
          </li>
          <li>
            <span className="num" aria-hidden>
              04
            </span>
            <span className="main">
              <span className="primary">Pin the black K to your toolbar, click it, and paste a connection code from below.</span>
            </span>
          </li>
        </ol>
      </section>

      <section className="section" aria-labelledby="connect-h">
        <div className="section-head">
          <h2 id="connect-h" className="label">
            Connection codes
          </h2>
          {tokens && <span className="label figures">{pad(tokens.length)} connected</span>}
        </div>
        <p className="hint">
          One per browser. A code lets the extension look up, add and shortlist candidates as you. It can't see billing, your team
          or settings. Turn one off here if a computer is lost or you stop using it.
        </p>

        {created ? (
          <div className="form copy-block">
            <p className="notice ok">Paste this into the extension now. It won't be shown again.</p>
            <div className="copy-row">
              <code>{created}</code>
              <button
                type="button"
                className="btn secondary"
                onClick={() =>
                  void navigator.clipboard.writeText(created).then(() => {
                    setCopied(true);
                  })
                }
              >
                {copied ? "Copied" : "Copy code"}
              </button>
            </div>
            <div className="button-row">
              <button type="button" className="link-btn" onClick={() => setCreated(null)}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className="inline-form" onSubmit={create}>
            <Field label="Which browser" value={label} maxLength={100} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Chrome on the office PC" />
            <span />
            <button className="btn" disabled={act.pending}>
              Create connection code
            </button>
          </form>
        )}

        <ErrorNotice error={load.error ?? act.error} />
        {tokens && tokens.length > 0 && (
          <ol className="index">
            {tokens.map((t, i) => (
              <li key={t.id}>
                <span className="num" aria-hidden>
                  {pad(i + 1)}
                </span>
                <span className="main">
                  <span className="primary">{t.label}</span>
                  <span className="secondary">
                    Created {whenLast(t.createdAt)} · {t.lastUsedAt ? `last used ${whenLast(t.lastUsedAt)}` : "not used yet"}
                  </span>
                </span>
                <span className="actions">
                  <button type="button" className="link-btn danger" disabled={act.pending} onClick={() => revoke(t)}>
                    Turn off
                  </button>
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="section" aria-labelledby="fair-h">
        <div className="section-head">
          <h2 id="fair-h" className="label">
            Good to know
          </h2>
        </div>
        <ul className="plain-list">
          <li>It reads only the page you have open, and only while the panel is showing. It never browses or collects profiles in the background.</li>
          <li>LinkedIn's terms don't allow tools that read its pages. Used one profile at a time like this, the realistic risk is a warning on your own LinkedIn account.</li>
          <li>LinkedIn doesn't show email addresses or phone numbers, so add those yourself if you have them.</li>
          <li>People you add this way go on your privacy-notice list on Today. Send them your notice within a month.</li>
        </ul>
      </section>
    </>
  );
}
